// src/taskpane/modules/word.ts
// [Service Layer] Word API Interaction
/* global Word, Office, HTMLTextAreaElement, HTMLButtonElement, console, setTimeout, window, HTMLElement */
import { DOM, BASE_KEYS, ICONS, ContextData, I18N_DICT } from "./store";
import { getEl, getSpacedKey, Logger, updateFeedback } from "./view";

// Helper for status updates since updateStatus was merged into updateFeedback
function updateStatus(msg: string, isErr?: boolean) {
    updateFeedback(DOM.STATUS_BAR, msg, isErr);
}

export async function initDocumentIdentity(): Promise<string> {
    return Word.run(async (context) => {
        const settings = context.document.settings;
        const idSetting = settings.getItemOrNullObject("bid_doc_uuid");
        idSetting.load("value");
        await context.sync();
        if (idSetting.isNullObject || !idSetting.value) {
            const newId = Date.now().toString(36) + Math.random().toString(36).substr(2);
            settings.add("bid_doc_uuid", newId);
            context.document.save(); await context.sync();
            Logger.info(`[Init] 新文档 ID: ${newId}`); return newId;
        } else {
            Logger.info(`[Init] 旧文档 ID: ${idSetting.value}`); return idSetting.value;
        }
    });
}

export function getOutlineLevel(prop: any): number { 
    if (!prop || prop === "BodyText") return 0; 
    try { 
        const match = prop.toString().match(/\d+/);
        return match ? parseInt(match[0]) : 0; 
    } catch { return 0; } 
}

function parseParagraphInfo(p: Word.Paragraph, isFocus: boolean, selText: string): ContextData & { displayLine: string; levelNum: number } {
    let num = ""; 
    try { if (p.listItemOrNullObject && !p.listItemOrNullObject.isNullObject) num = p.listItemOrNullObject.listString || ""; } catch {}
    const raw = selText || p.text; 
    const fullText = (num ? `${num} ` : "") + (raw || "").trim();
    const lvl = getOutlineLevel(p.outlineLevel); 
    const isHeading = lvl >= 1 && lvl <= 9;
    const badgeHtml = isHeading ? `<span class="crumb-badge lvl-${lvl}">H${lvl}</span>` : `<span class="crumb-badge">Body</span>`;
    const html = `<div style="display:flex; align-items:center;">${badgeHtml}<span class="crumb-text ${isFocus ? 'current' : ''}">${fullText}</span></div>`;
    return { fullText, levelNum: lvl, isHeading, displayLine: html, text: fullText, breadcrumbHtml: html };
}

export async function getWordContext(): Promise<ContextData> {
    return Word.run(async (context) => {
        const selection = context.document.getSelection(); 
        selection.load(["text"]);
        
        let p = selection.paragraphs.getFirst(); 
        p.load(["text", "outlineLevel", "listItemOrNullObject/listString"]);
        await context.sync();
        
        const info = parseParagraphInfo(p, true, selection.text);
        Logger.info(`[Word] 选区: "${info.fullText.slice(0, 30)}..." (H${info.levelNum}, Title:${info.isHeading})`);
        
        let breadcrumbLines = [info.displayLine]; 
        let pathLines = [info.fullText];

        let searchLevel = info.levelNum === 0 ? 10 : info.levelNum;
        
        if (searchLevel > 1) {
            const rangeBefore = p.getRange("Start").expandTo(context.document.body.getRange("Start")); 
            const parasBefore = rangeBefore.paragraphs; 
            parasBefore.load("outlineLevel"); 
            await context.sync();
            
            let items = parasBefore.items; 
            let steps = 0; 
            
            for (let i = items.length - 1; i >= 0; i--) {
                steps++; 
                if (steps > 500) break; 
                const lvl = getOutlineLevel(items[i].outlineLevel);
                if (lvl >= 1 && lvl < searchLevel) {
                    items[i].load(["text", "listItemOrNullObject/listString"]); 
                    await context.sync();
                    const parentInfo = parseParagraphInfo(items[i], false, "");
                    breadcrumbLines.unshift(parentInfo.displayLine);
                    pathLines.unshift(parentInfo.fullText); 
                    searchLevel = lvl; 
                    if (lvl === 1) break; 
                }
            }
        }
        
        return { 
            text: info.fullText, 
            breadcrumbHtml: breadcrumbLines.join("<br>"), 
            isHeading: info.isHeading, 
            fullPath: pathLines.join("\n"), 
            fullText: info.fullText 
        };
    });
}

// [新增] 1. 定义文本片段接口
interface TextSegment {
    text: string;
    bold: boolean;
}

// [新增] 2. 解析器：将文本切分为普通片段和加粗片段，正则微调，增强鲁棒性
function parseTextToSegments(text: string): TextSegment[] {
    const segments: TextSegment[] = [];
    // 使用 [\s\S] 替代 . 以确保匹配所有字符（尽管我们在外部split了行，这样写更安全）
    const regex = /\*\*((?:[\s\S]*?))\*\*/g; 
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
        // 普通文本
        if (match.index > lastIndex) {
            segments.push({
                text: text.substring(lastIndex, match.index),
                bold: false
            });
        }
        // 加粗文本
        if (match[1]) {
            segments.push({
                text: match[1],
                bold: true
            });
        }
        lastIndex = regex.lastIndex;
    }

    // 剩余文本
    if (lastIndex < text.length) {
        segments.push({
            text: text.substring(lastIndex),
            bold: false
        });
    }

    return segments;
}

// 3. 正文核心插入函数

export async function insertToDoc() {
    const elOutput = getEl<HTMLTextAreaElement>(DOM.FINAL_OUTPUT);
    const rawText = elOutput?.value || "";
    
    if (!rawText) { 
        Logger.info("[Word] 插入空文本取消"); 
        return; 
    }

    // --- Sanitizer: 结构标准化与清洗 ---
    // 1. 基础清洗：移除 Markdown 块级符号，保留行内 **，统一换行符
    let cleanText = rawText
        .replace(/^#+\s+/gm, "")      // 移除 Markdown 标题符
        .replace(/^>+\s+/gm, "")      // 移除引用符
        .replace(/^```\w*\s*$/gm, "") // 移除代码块
        .replace(/\r\n/g, "\n");      // 统一换行符标准

    // 2. 智能结构补全 (Smart Splitter)
    // 逻辑：捕获 (前置字符)(空格)(加粗标)(列表头)
    // 目的：在列表项前自动补全换行符，但要避开 "词组" 和 "Markdown标记破坏"
    // [Fix] Added (?!\d) to prevent matching version numbers (V8.4) or decimals (99.99%)
    const structureRegex = /([^\n])(\s*)(\**)((?:[1-9]\d*\.(?!\d)|[一二三四五六七八九十〇]+、))/g;

    cleanText = cleanText.replace(structureRegex, (match, p1, p2, p3, p4) => {
        // p1: 前置字符 
        // p2: 可能的空格
        // p3: 可能的加粗符 "**" (或被错误切分的 "*")
        // p4: 列表头 (如 "1.", "一、")

        // [关键防御逻辑]：防止切断 Markdown 加粗标记
        // 如果 p1 是 '*' 且 p3 包含 '*'，说明正则错误地把 '**' 切分成了 p1 和 p3
        // 此时绝对不能插入换行，必须保持原样
        if (p1 === '*' && p3.includes('*')) {
            return match; 
        }

        // [中文词组防御逻辑]
        // 如果是 "中文列表头" 且 "紧接在汉字后面" (如: 统一、)，保持原样
        const isChineseMarker = /^[一二三四五六七八九十〇]+、/.test(p4);
        const isPrecedingCharChinese = /[\u4e00-\u9fa5]/.test(p1);

        if (isChineseMarker && isPrecedingCharChinese) {
            return match; 
        }

        // 其他情况：强制在列表头前加换行符
        return `${p1}\n${p2}${p3}${p4}`;
    });

    // --- [行处理] ---
    // 1. split("\n"): 按行切分
    // 2. map(t => t.trim()): 去除每一行的首尾空白
    // 3. filter(t => t !== ""): 彻底剔除空行，防止 Word 插入空段落
    const lines = cleanText
        .split("\n")
        .map(line => line.trim()) 
        .filter(line => line.length > 0);

    Logger.info(`[Word] 准备插入 ${lines.length} 行正文 (已过滤空行)`);

    return Word.run(async (context) => {

        context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
        const selection = context.document.getSelection();
        const currentPara = selection.paragraphs.getFirst();
        
        // [关键] 追踪锚点段落，防止在插入过程中丢失引用
        context.trackedObjects.add(currentPara);
        
        currentPara.load(["outlineLevel"]);
        await context.sync();

        const lvl = getOutlineLevel(currentPara.outlineLevel);
        const isHeading = lvl >= 1 && lvl <= 9;

        // --- 策略 A: 光标在正文中 (替换模式) ---
        // 意图通常是“替换”或“续写”。为了避免格式混乱，先清空选区。
        if (!isHeading) {
            selection.insertText("", Word.InsertLocation.replace);
            await context.sync();
        }
        
        // --- 策略 B: 流式下推插入 ---
        // 无论是在标题还是正文后，都在锚点之后追加新段落
        let anchorPara = currentPara;

        for (let i = 0; i < lines.length; i++) {
            let lineText = lines[i];
            // 编号后空格清洗
            lineText = lineText.replace(/^(\s*)(\**)(\d+\.|[一二三四五六七八九十〇]+、)\s+(?=\S)/, "$1$2$3");
            // 1. 插入新段落
            const newPara = anchorPara.insertParagraph("", Word.InsertLocation.after);
            
            // [对象追踪强化] 立即追踪新创建的段落对象
            context.trackedObjects.add(newPara);
            
            // 2. 样式重置 (关键)
            // 强制重置为正文样式 (Normal)，并清除缩进，防止继承标题样式
            newPara.styleBuiltIn = Word.BuiltInStyleName.normal;
            newPara.leftIndent = 0;

            // 3. Token 化渲染 (处理行内加粗)
            // 解析当前行，识别 **，逐片插入并应用样式
            const segments = parseTextToSegments(lineText);
            
            for (const seg of segments) {
                const range = newPara.insertText(seg.text, Word.InsertLocation.end);
                // 显式设置粗体属性
                range.font.bold = seg.bold;
            }

            // 4. 更新锚点
            anchorPara = newPara;
            
            // 性能优化：每隔 50 行同步一次，防止请求积压
            if (i % 50 === 0) {
                try {
                    await context.sync();
                } catch (error) {
                    Logger.error(`[Word] context.sync() 失败 (第 ${i} 行): ${error.message}`);
                    throw error;
                }
            }
        }

        // 清理追踪对象：移除所有新创建的段落对象
        // 注意：currentPara 是原始锚点段落，也需要清理
        context.trackedObjects.remove(currentPara);
        try {
            await context.sync();
        } catch (error) {
            Logger.error(`[Word] 最终 context.sync() 失败: ${error.message}`);
            throw error;
        }
    });
}


// --- [插入大纲辅助函数] ---

interface FlatNode {
    type: 'heading' | 'instruction' | 'text';
    relLevel: number;
    text: string;
}

/**
 * 解析 Markdown 为扁平化的节点列表
 */
function parseMarkdownToFlatNodes(text: string): FlatNode[] {
    const cleanText = text.replace(/\r\n/g, "\n").replace(/[\u200B-\u200D\uFEFF]/g, "");
    const lines = cleanText.split("\n");
    const nodes: FlatNode[] = [];

    for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;

        // 匹配标准标题行
        const headingMatch = trimmed.match(/^(#+)\s*(.+?)(?:\s*(?:\[|【)(.*?)(?:\]|】))?$/);
        if (headingMatch) {
            const level = headingMatch[1].length;
            // [修改 1] 提取标题文本
            let titleText = headingMatch[2].trim();
            // 将 "1.  项目背景" 转换为 "1.项目背景"
            titleText = titleText.replace(/^(\d+\.|[一二三四五六七八九十〇]+、)\s+(?=\S)/, "$1");

            // [修改 2] 使用清洗后的 titleText
            nodes.push({ type: 'heading', relLevel: level, text: titleText });
            if (headingMatch[3]) {
                nodes.push({ type: 'instruction', relLevel: level, text: headingMatch[3].trim() });
            }
            continue;
        }

        // 匹配独立指导语
        const guideMatch = trimmed.match(/^(?:\[|【)(.*?)(?:\]|】)$/);
        if (guideMatch) {
            nodes.push({ type: 'instruction', relLevel: 0, text: guideMatch[1].trim() });
            continue;
        }

        // 普通文本
        nodes.push({ type: 'text', relLevel: 0, text: trimmed });
    }
    return nodes;
}

// --- [主函数] ---

export async function insertMarkdownOutline(markdownText: string) {
    if (!markdownText) { Logger.info("[Word] 大纲文本为空，取消插入"); return; }
    
    // 1. 解析并反转 (倒序插入策略)
    const nodes = parseMarkdownToFlatNodes(markdownText).reverse();
    Logger.info(`[Word] 准备插入 ${nodes.length} 个节点`);

    return Word.run(async (context) => {
        context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
        const selection = context.document.getSelection();
        const currentPara = selection.paragraphs.getFirst();
        
        context.trackedObjects.add(currentPara);
        currentPara.load(["outlineLevel"]);
        await context.sync();

        const baseLevel = getOutlineLevel(currentPara.outlineLevel);
        
        for (const node of nodes) {
            let targetLevel = 0;
            if (node.type === 'heading') {
                targetLevel = Math.min(baseLevel + node.relLevel, 9);
            } else if (node.type === 'instruction') {
                const lvl = node.relLevel > 0 ? node.relLevel : 1; 
                targetLevel = Math.min(baseLevel + lvl, 9);
            }

            // 准备文本
            let textToInsert = node.text;
            if (node.type === 'instruction') textToInsert = `[${node.text}]`;
            
            const newPara = currentPara.insertParagraph(textToInsert, Word.InsertLocation.after);
            
            // [对象追踪强化] 立即追踪新创建的段落对象
            context.trackedObjects.add(newPara);
            
            newPara.load("font"); 
            await context.sync(); 

            if (node.type === 'heading') {
                if (targetLevel > 0) {
                    try {
                        const styleEnum = `Heading${targetLevel}` as Word.BuiltInStyleName;
                        newPara.styleBuiltIn = styleEnum;
                    } catch (e) {
                        newPara.styleBuiltIn = Word.BuiltInStyleName.normal;
                        newPara.font.bold = true;
                        newPara.leftIndent = (targetLevel - 1) * 28;
                    }
                } else {
                    newPara.styleBuiltIn = Word.BuiltInStyleName.normal;
                }
            } 
            else if (node.type === 'instruction') {
                newPara.styleBuiltIn = Word.BuiltInStyleName.normal;
                const indent = targetLevel > 0 ? targetLevel : 1;
                newPara.leftIndent = indent * 28;
                newPara.font.italic = true; // 加个斜体区分
                newPara.font.color = "#555555"; // 加个灰色区分
            } 
            else {
                newPara.styleBuiltIn = Word.BuiltInStyleName.normal;
            }

            await context.sync(); // 提交样式修改
        }

        context.trackedObjects.remove(currentPara);
        try {
            await context.sync();
        } catch (error) {
            Logger.error(`[Word] 大纲插入最终 context.sync() 失败: ${error.message}`);
            throw error;
        }
    });

}

export function loadTaskConfig(currentDocId: string) {
    updateFeedback(DOM.TASK_FEEDBACK, ""); 
    Word.run(async (context) => {
        const settings = context.document.settings;
        const redBookSetting = settings.getItemOrNullObject("bid_redbook");
        const rulesSetting = settings.getItemOrNullObject("bid_rules");
        redBookSetting.load("value"); rulesSetting.load("value");
        await context.sync();

        const elRedBook = getEl<HTMLTextAreaElement>(DOM.RED_BOOK);
        const elRules = getEl<HTMLTextAreaElement>(DOM.GLOBAL_RULES);
        
        let redBookVal = !redBookSetting.isNullObject ? redBookSetting.value : "";
        let rulesVal = !rulesSetting.isNullObject ? rulesSetting.value : "";
        
        const localRedBook = localStorage.getItem(getSpacedKey(BASE_KEYS.REDBOOK, currentDocId));
        const localRules = localStorage.getItem(getSpacedKey(BASE_KEYS.RULES, currentDocId));
        
        if (!redBookVal && localRedBook) redBookVal = localRedBook;
        if (!rulesVal && localRules) rulesVal = localRules;

        if (elRedBook) elRedBook.value = redBookVal;
        if (elRules) elRules.value = rulesVal;
        if (elRedBook) localStorage.setItem(getSpacedKey(BASE_KEYS.REDBOOK, currentDocId), elRedBook.value);
        if (elRules) localStorage.setItem(getSpacedKey(BASE_KEYS.RULES, currentDocId), elRules.value);
    }).catch(e => console.error("Config Load:", e));
}

export async function saveTaskConfig(currentDocId: string) {
    const elRedBook = getEl<HTMLTextAreaElement>(DOM.RED_BOOK);
    const elRules = getEl<HTMLTextAreaElement>(DOM.GLOBAL_RULES);
    const btnSave = getEl<HTMLButtonElement>(DOM.BTN_SAVE_TASK);
    if (!elRedBook || !elRules) return;

    if (btnSave) btnSave.disabled = true;
    updateFeedback(DOM.TASK_FEEDBACK, ""); 

    try {
        await Word.run(async (context) => {
            context.document.settings.add("bid_redbook", elRedBook.value);
            context.document.settings.add("bid_rules", elRules.value);
            await context.sync(); 
        });
        localStorage.setItem(getSpacedKey(BASE_KEYS.REDBOOK, currentDocId), elRedBook.value);
        localStorage.setItem(getSpacedKey(BASE_KEYS.RULES, currentDocId), elRules.value);    
        updateFeedback(DOM.TASK_FEEDBACK, "已保存到文档", false);
        setTimeout(() => updateFeedback(DOM.TASK_FEEDBACK, ""), 3000);
    } catch (error) {
        console.error("Save config failed:", error);
        updateFeedback(DOM.TASK_FEEDBACK, "保存失败: " + error.message, true);
    } finally {
        if (btnSave) btnSave.disabled = false;
    }
}

export function cleanDocumentMetadata(currentDocId: string, lang: "en" | "zh", resetBtnCallback: (btn: HTMLElement) => void) {
    const btn = getEl<HTMLElement>(DOM.BTN_CLEAN_DOC); 
    if(!btn) return;
    const dict = I18N_DICT[lang];

    // 第一次点击：进入确认状态
    if(!btn.classList.contains("confirm-state")) { 
        btn.innerHTML = `<span style="font-weight:600; font-size:11px;">${dict["btn-clean-confirm"]}</span>`; 
        btn.classList.add("confirm-state"); 
        
        // 3秒后如果没有再次点击，自动恢复原状
        setTimeout(() => resetBtnCallback(btn), 3000); 
        return; 
    }

    // 第二次点击：执行真正的净化
    btn.innerHTML = `<span style="font-size:11px;">${dict["status-cleaning"]}</span>`; 
    
    // 清除元数据逻辑 (保持不变)
    Office.context.document.settings.remove("bid_rules"); 
    Office.context.document.settings.remove("bid_redbook");
    Office.context.document.settings.remove("bid_doc_uuid");
    localStorage.removeItem(getSpacedKey(BASE_KEYS.REDBOOK, currentDocId));
    localStorage.removeItem(getSpacedKey(BASE_KEYS.RULES, currentDocId));

    Office.context.document.settings.saveAsync(r => { 
        if(r.status === Office.AsyncResultStatus.Succeeded) { 
            // 成功反馈：变为绿色对勾
            btn.innerHTML = ICONS.SUCCESS; 
            btn.style.color = "#107c10"; 
            updateFeedback(DOM.STATUS_BAR, dict["status-clean-done"], false);
        } else { 
            // 失败反馈：变为红色叉号
            btn.innerHTML = ICONS.ERROR; 
            btn.style.color = "#d13438"; 
        } 
        // 2秒后彻底恢复初始状态
        setTimeout(() => { resetBtnCallback(btn); }, 2000); 
    });
}

/**
 * 获取任务上下文：如果是空选区，自动扩展到当前段落
 */
export async function getSmartSelection(): Promise<{ text: string, isExpanded: boolean }> {
    return Word.run(async (context) => {
        let selection = context.document.getSelection();
        selection.load(["text", "isEmpty"]); // 关键：加载 isEmpty 属性
        await context.sync();

        let isExpanded = false;

        // 核心逻辑：如果是空的（光标状态），自动扩展到整个段落
        // 注意：这里我们判断 text 是否为空字符串更保险，有的版本 isEmpty 行为不一致
        if (!selection.text || selection.text.trim().length === 0) {
            // 扩展选区到段落
            selection = selection.paragraphs.getFirst().getRange();
            selection.select(); // [可选] UI 上是否要让用户看到选中效果？建议选中，给用户反馈
            selection.load("text");
            isExpanded = true;
            await context.sync();
        }

        return { 
            text: selection.text, 
            isExpanded 
        };
    });
}
